'use client';

import { useEffect, useState } from 'react';
import { GitCompare, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useCompareStore, type CompareItem } from '@/stores/compare';

interface Props {
  item: CompareItem;
  /** Compact = small chip beside other action buttons. Default = labeled. */
  compact?: boolean;
}

export function CompareToggleButton({ item, compact }: Props) {
  const toggle = useCompareStore((s) => s.toggle);
  const has = useCompareStore((s) => s.has);
  const items = useCompareStore((s) => s.items);

  // Hydration guard so SSR HTML matches client first render
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const inCompare = mounted && has(item.id);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const added = toggle(item);
    if (!added && !inCompare) {
      // toggle returned false AND we weren't in compare → cap reached
      toast.error(`Max ${4} produkter att jämföra. Ta bort en först.`);
      return;
    }
    if (added) {
      toast.success(`Lagt till i jämförelse (${items.length + 1} valda)`, {
        action: { label: 'Jämför nu', onClick: () => { window.location.href = '/jamfor'; } },
        duration: 4000,
      });
    } else {
      toast(`Borttagen från jämförelse`);
    }
  };

  if (compact) {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-label={inCompare ? 'Ta bort från jämförelse' : 'Lägg till i jämförelse'}
        className={`inline-flex items-center justify-center h-8 w-8 rounded-full border transition-all ${
          inCompare
            ? 'bg-brand border-brand text-white'
            : 'bg-white/90 border-gray-200 text-gray-600 hover:border-brand hover:text-brand'
        }`}
      >
        {inCompare ? <Check className="h-4 w-4" /> : <GitCompare className="h-4 w-4" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
        inCompare
          ? 'bg-brand border-brand text-white'
          : 'bg-white border-gray-200 text-gray-700 hover:border-brand hover:text-brand'
      }`}
    >
      {inCompare ? <Check className="h-4 w-4" /> : <GitCompare className="h-4 w-4" />}
      {inCompare ? 'I jämförelse' : 'Jämför'}
    </button>
  );
}
